

// File: LogsChatAssistantBox.js
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { X, Maximize2, Paperclip  } from 'react-feather';
import { FaBookBookmark, FaWandMagicSparkles } from 'react-icons/fa6';



// ====== Helpers: minimal rich text renderer (bold + bullets + numbered) ======
function renderBold(str) {
  return str.split(/(\*\*[^*]+\*\*)/g).map((seg, i) => {
    const m = seg.match(/^\*\*([^*]+)\*\*$/);
    if (m) return <strong key={i} style={{ fontWeight: 600 }}>{m[1]}</strong>;
    return <span key={i}>{seg}</span>;
  });
}

// Insert line breaks before inline bullets/numbered markers
function preprocessForLists(text) {
  if (!text) return '';
  let s = String(text);
  s = s.replace(/\s-\s+/g, '\n- ');        // bullets
  s = s.replace(/\s(\d+)\.\s+/g, '\n$1. '); // numbered
  return s;
}

function RichText({ text, color = 'inherit' }) {
  if (!text) return null;

    const wrap = { wordBreak: 'break-word', overflowWrap: 'anywhere' };


  // Build styles with dynamic color; default = inherit (so it follows the bubble)
  const stylesRT = {
   p:  { margin: '0 0 6px 0', lineHeight: 1.35, color, ...wrap },
 ul: { margin: '4px 0 0 16px', padding: 0, color, ...wrap },
 ol: { margin: '4px 0 0 16px', padding: 0, color, ...wrap }
  };

  const prepped = preprocessForLists(text);
  const lines = prepped.split(/\n+/);
  const nodes = [];

  let listType = null; // 'ul' | 'ol' | null
  let listItems = [];

  const flushList = (keyBase) => {
    if (!listType || listItems.length === 0) return;
    if (listType === 'ul') {
      nodes.push(
        <ul key={`ul-${keyBase}`} style={stylesRT.ul}>
{listItems.map((li, i) => <li key={i} style={wrap}>{renderBold(li)}</li>)}
        </ul>
      );
    } else {
      nodes.push(
        <ol key={`ol-${keyBase}`} style={stylesRT.ol}>
{listItems.map((li, i) => <li key={i} style={wrap}>{renderBold(li)}</li>)}
        </ol>
      );
    }
    listType = null;
    listItems = [];
  };

  lines.forEach((raw, idx) => {
    const line = raw.trim();
    if (!line) return;

    if (/^-\s+/.test(line)) {
      const content = line.replace(/^-+\s+/, '');
      if (listType && listType !== 'ul') flushList(idx);
      listType = 'ul';
      listItems.push(content);
      return;
    }

    const numMatch = line.match(/^(\d+)\.\s+(.*)$/);
    if (numMatch) {
      const content = numMatch[2];
      if (listType && listType !== 'ol') flushList(idx);
      listType = 'ol';
      listItems.push(content);
      return;
    }

    flushList(idx);
    nodes.push(<p key={`p-${idx}`} style={stylesRT.p}>{renderBold(line)}</p>);
  });

  flushList('end');
  return <>{nodes}</>;
}





// for s3
// robust S3 -> UI attachment mapping (handles missing/incorrect MIME)
function mapAttachmentsFromHistory(atts = []) {
  return atts.map(a => {
    const mime = (a.mimetype || '').toLowerCase();
    const nameKey = (a.name || a.key || '').toLowerCase();

    const isImg = mime.startsWith('image/')
      || /\.(png|jpe?g|gif|webp|bmp|svg)$/.test(nameKey);

    const isAud = mime.startsWith('audio/')
      || /\.(mp3|m4a|aac|wav|ogg|oga|flac|opus)$/.test(nameKey);

    return {
      name: a.name || a.key || 'file',
      url: a.url,
      type: isImg ? 'image' : (isAud ? 'audio' : 'file')
    };
  });
}





const NOT_FOUND_RE = /no\s+user\s+in\s+crm\s+found/i;

// ====== Spinner, CSS, Mobile ======
const spinnerStyle = {
  width: 48, height: 48, border: '4px solid #e5e7eb',
  borderTop: '4px solid #6366f1', borderRadius: '50%',
  animation: 'spin 1s linear infinite'
};
const keyframes = `
@keyframes spin { 0%{transform:rotate(0)} 100%{transform:rotate(360deg)} }
`;
const wandCSS = `
.ai-wand-btn { outline: none !important; -webkit-tap-highlight-color: transparent; }
.ai-wand-btn:focus,
.ai-wand-btn:focus-visible,
.ai-wand-btn:active { outline: none !important; }
`;
const mobileChatCSS = `
@media (max-width: 600px) {
  .ai-chat-wrapper {
    right: 0 !important;
    bottom: 0 !important;
    width: 100vw !important;
    height: 90vh !important;
    min-width: 0 !important;
    z-index: 2001;
  }
  .ai-wand-btn {
    right: 30px !important;
    top: 0px !important;
    width: 56px !important;
    height: 56px !important;
    min-width: 46px !important;
    min-height: 46px !important;
    z-index: 2100 !important;
    display: flex !important;
    pointer-events: auto !important;
  }
  .ai-chat-panel {
    width: 100vw !important;
    height: 75vh !important;
    min-width: 0 !important;
  }
}
`;

export default function LogsChatAssistantBox({
  isOpen,
  onClose,
  sessionId,      // Twilio CallSid (or psid)
  phoneForQuery,  // normalized phone used in seed query
  toNumber,       // NEW
  fromNumber      // NEW
}) {
  const [bootLoading, setBootLoading] = useState(false); // loader for /session-history
  const [loading, setLoading] = useState(false);         // "Typing..." during agent calls
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
const fileInputRef = useRef(null);


  const [currentSession, setCurrentSession] = useState(sessionId || '');
  const [collapsed, setCollapsed] = useState(false);
  const [headerText, setHeaderText] = useState('');      // NEW: dynamic header content
  const [hasHeaderData, setHasHeaderData] = useState(false);
  const messageEndRef = useRef(null);

  const [seedLoading, setSeedLoading] = useState(false); // NEW: initial agent-query waiting text


  useEffect(() => {
    if (!isOpen) return;
    setCurrentSession(sessionId || '');
    setMessages([]);
    setInput('');
    setCollapsed(false);
    setHeaderText('');
    setHasHeaderData(false);
    fetchHistoryOrSeed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, sessionId]);

  useEffect(() => {
    if (messageEndRef.current) {
      messageEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, loading]);

  const makeAttachmentPreviews = (files) => Array.from(files).map(f => {
  const url = URL.createObjectURL(f);
  const t = (f.type || '').toLowerCase();
  const isImg = t.startsWith('image/'); const isAud = t.startsWith('audio/');
  return { name: f.name, url, type: isImg ? 'image' : (isAud ? 'audio' : 'file') };
});
const clearFileInput = () => { setSelectedFiles([]); if (fileInputRef.current) fileInputRef.current.value = ''; };

  const authHeaders = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('token')}`
  };

  // Choose your base URL (you currently use localhost for dev in this file)
  // const BASE = 'http://localhost:8000'; //For local
  const BASE = 'https://bot.mergeasy.com'; //For production

  // open full-screen chat in new tab using the same session & phone
const openFullScreen = () => {
  const id = encodeURIComponent(currentSession || sessionId || '');
  const phone = encodeURIComponent(phoneForQuery || '');

  const toQ = encodeURIComponent(toNumber || '');
  const fromQ = encodeURIComponent(fromNumber || '');


  // const url = `/ai/chat/${id}?phone=${phone}`;
  const url = `/ai/chat/${id}?phone=${phone}&to=${toQ}&from=${fromQ}`;

  window.open(url, '_blank', 'noopener,noreferrer');
};


  const maybeSetHeaderFromText = (txt) => {
    if (!txt) return;
    if (!NOT_FOUND_RE.test(String(txt))) {
      setHeaderText(String(txt));
      setHasHeaderData(true);
    }
  };

  const fetchHistoryOrSeed = async () => {
    try {
      setBootLoading(true);
      // 1) Try history first
      const hist = await axios.post(
        `${BASE}/chat/session-history`,
        { session_id: sessionId },
        { headers: authHeaders }
      );

      const items = hist?.data?.items || [];
      if (Array.isArray(items) && items.length > 0) {
        const mapped = [];
        for (const it of items) {
         


     const hasAtts = Array.isArray(it.attachments) && it.attachments.length > 0;
if ((it.query && it.query.trim() !== '') || hasAtts) {
  mapped.push({
    sender: 'user',
    text: it.query || '',                               // allow empty text
    attachments: mapAttachmentsFromHistory(it.attachments || [])
  });
}



if (it.response) {
  mapped.push({
    sender: 'bot',
    text: it.response,
    response_id: it.id,
    podio_refs: it.podio_refs,
    saved: false
  });
}

        }
        setMessages(mapped);

        // NEW: pick the first bot message (ascending) as header, if not "not found"
        const firstBot = mapped.find(m => m.sender === 'bot');
        if (firstBot) maybeSetHeaderFromText(firstBot.text);

        setBootLoading(false);
      } else {
        // 2) No history → seed initial query
        setBootLoading(false);
        await seedInitialQuery();
      }
    } catch (err) {
      console.error('History fetch failed:', err);
      setBootLoading(false);
      await seedInitialQuery();
    }
  };

  // Do NOT render the initial user message; only show bot reply
  const seedInitialQuery = async () => {
    const text = `Tell the details of the item whose phone number is ${phoneForQuery}, if found nothing then send Just mentioning - No User In CRM Found.?`; //for production
        // const text = `Tell the details of the item whose phone number is 18989898989, if found nothing then send Just mentioning - No User In CRM Found.?`; //for testing

      setSeedLoading(true);   // NEW

    setLoading(true);
    try {



      const res = await axios.post(
        `${BASE}/agent-query`,
        // { text, session_id: sessionId },
          {
      text,
      session_id: sessionId,
      to: toNumber || '',
      from: fromNumber || '',
      ai_sense:"ai_chat_bot"
    },

        { headers: authHeaders }
      );
      const botMsg = res?.data?.response || 'No response';
      const podioRefs = res?.data?.podio_refs || null;
      const responseId = res?.data?.response_id || null;

      // Only bot message
      setMessages(prev => [
        ...prev,
        { sender: 'bot', text: botMsg, podio_refs: podioRefs, response_id: responseId, saved: false }
      ]);

      // NEW: set header if it looks like a "found" response
      maybeSetHeaderFromText(botMsg);

      setCurrentSession(res?.data?.session_id || sessionId || '');
    } catch (err) {
      console.error('Initial seed failed:', err);
      setMessages(prev => [...prev, { sender: 'bot', text: 'Something went wrong while starting the session.' }]);
    } finally {
       setSeedLoading(false); // NEW
      setLoading(false);
    }
  };

  const sendQuery = async (text) => {
    setLoading(true);
    try {


        let res;
  if (selectedFiles.length > 0) {
    const form = new FormData();
    form.append('text', text || '');
    form.append('session_id', currentSession || sessionId);
    form.append('to', toNumber || '');
    form.append('from', fromNumber || '');
    form.append('ai_sense', "ai_chat_bot");

    selectedFiles.forEach(f => form.append('files', f, f.name));
    const previews = makeAttachmentPreviews(selectedFiles);
    setMessages(prev => [...prev, { sender: 'user', text, attachments: previews }]);
    res = await axios.post(`${BASE}/agent-query`, form, { headers: { 'Authorization': authHeaders.Authorization } });
    clearFileInput();
  } else {





    setMessages(prev => [...prev, { sender: 'user', text }]);
    res = await axios.post(`${BASE}/agent-query`, 
      // { text, session_id: currentSession || sessionId }, 
            {
        text,
        session_id: currentSession || sessionId,
        to: toNumber || '',
        from: fromNumber || '',
        ai_sense:"ai_chat_bot"
      },
      
      { headers: authHeaders });
  }


      const botMsg = res?.data?.response || 'No response';
      const podioRefs = res?.data?.podio_refs || null;
      const responseId = res?.data?.response_id || null;

      setMessages(prev => [
        ...prev,
        { sender: 'bot', text: botMsg, podio_refs: podioRefs, response_id: responseId, saved: false }
      ]);
      if (res?.data?.session_id) setCurrentSession(res.data.session_id);

      // Optional: if we didn't have header yet but now we do, set it once
      // if (!hasHeaderData) maybeSetHeaderFromText(botMsg);
    } catch (err) {
      console.error('sendQuery failed:', err);
      setMessages(prev => [...prev, { sender: 'bot', text: 'Something went wrong.' }]);
    } finally {
      setLoading(false);
      setSelectedFiles([])
    }
  };

  const handleSaveToKnowledgebase = async (responseId, podioRefs, index) => {
    try {
      const res = await axios.post(
        `${BASE}/podio/index-by-response`,
        { response_id: responseId, podio_refs: podioRefs },
        { headers: authHeaders }
      );
      if (res.status === 200) {
        const next = [...messages];
        next[index].saved = true;
        setMessages(next);
      }
    } catch (err) {
      console.error('Failed to save to KB:', err);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <style>{keyframes}</style>
      <style>{wandCSS}</style>
      <style>{mobileChatCSS}</style>

      {/* Wrapper holds the wand button and animated panel */}
      <div
        className="ai-chat-wrapper"
        style={{
          position: 'fixed',
          right: '11vw',
          bottom: '8vh',
          width: 320,
          height: 560,
          zIndex: 2001,
          pointerEvents: 'none'
        }}
      >
        {/* Magic Wand Toggle Button */}
        <button
          className="ai-wand-btn"
          onClick={() => setCollapsed(c => !c)}
          title={collapsed ? 'Open Assistant' : 'Collapse Assistant'}
          style={{
            position: 'absolute',
            right: -145,
            width: 60,
            height: 60,
            borderRadius: '50%',
            border: 'none',
            cursor: 'pointer',
            pointerEvents: 'auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(135deg, #4144f5ff 0%, #37bcfaff 100%)',
            boxShadow: '0 10px 20px rgba(0,0,0,0.18), inset 0 0 12px rgba(255,255,255,0.22)',
            transition: 'transform 250ms ease, box-shadow 250ms ease, filter 250ms ease',
            transform: collapsed ? 'rotate(-12deg) scale(1.02)' : 'rotate(0deg) scale(1)',
            filter: collapsed ? 'brightness(1.05)' : 'none'
          }}
          onMouseDown={e => (e.currentTarget.style.transform = 'scale(0.96)')}
          onMouseUp={e => (e.currentTarget.style.transform = collapsed ? 'rotate(-12deg) scale(1.02)' : 'rotate(0deg) scale(1)')}
          onMouseLeave={e => (e.currentTarget.style.transform = collapsed ? 'rotate(-12deg) scale(1.02)' : 'rotate(0deg) scale(1)')}
        >
          <FaWandMagicSparkles size={18} color="#fff" />
        </button>

        {/* Animated Chat Panel */}
        <div
          className="ai-chat-panel"
          style={{
            pointerEvents: collapsed ? 'none' : 'auto',
            width: 400,
            height: 600,
            background: '#fff',
            borderRadius: 10,
            boxShadow: '0 15px 35px rgba(0,0,0,0.25)',
            display: 'flex',
            flexDirection: 'column',
            transformOrigin: 'top right',
            transform: collapsed ? 'scale(0.1)' : 'scale(1)',
            opacity: collapsed ? 0 : 1,
            transition: 'transform 300ms cubic-bezier(0.22, 1, 0.36, 1), opacity 220ms ease'
          }}
        >
          {/* Header (dynamic) */}
          <div style={{ background: '#f3f4f6', padding: 12 }}>
            {/* <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              {!hasHeaderData ? (
                <strong>✨🤖 AI Assistant</strong>
              ) : (
                <strong style={{ color: '#111827' }}>Conversation</strong>
              )}
         
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <Maximize2
                    title="Open in new tab"
                    onClick={openFullScreen}
                    style={{ cursor: 'pointer' }}
                    size={18}
                  />
                  <X
                    onClick={() => {
                      onClose();
                      setMessages([]);
                      setInput('');
                      setCollapsed(false);
                    }}
                    style={{ cursor: 'pointer' }}
                    size={18}
                  />
              </div>
            </div> */}

            {/* <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}> */}
            <div style={{ padding: 16, borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between'  }}>

                  {!hasHeaderData ? (
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#111827' }}>✨🤖 AI Assistant</h3>
              ) : (
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#111827' }}>Conversation</h3>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#6b7280' }}>
                 <Maximize2
                    title="Open in new tab"
                    onClick={openFullScreen}
                    style={{ cursor: 'pointer' }}
                    size={18}
                  />
                  <X
                    onClick={() => {
                      onClose();
                      setMessages([]);
                      setInput('');
                      setCollapsed(false);
                    }}
                    style={{ cursor: 'pointer' }}
                    size={18}
                  />
              </div>
            </div>

            {hasHeaderData && (
              <div
                style={{
                  marginTop: 8,
                  background: 'linear-gradient(180deg, #ffffff 0%, #f9fafb 100%)',
                  borderRadius: 12,
                  padding: 12,
                  border: '1px solid #e5e7eb',
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    height: '100%',
                    width: 6,
                    background: 'linear-gradient(180deg, #6366f1 0%, #22d3ee 100%)'
                  }}
                />
                <div style={{ marginLeft: 12, maxHeight: 140, overflow: 'auto' }}>
                  <RichText text={headerText} color="#111827"  />
                </div>
              </div>
            )}
          </div>

          {/* Body */}
          <div style={{ position: 'relative', flex: 1, padding: 12, overflowY: 'auto' }}>
            {bootLoading && (
              <div style={{
                position: 'absolute', inset: 0, display: 'flex',
                alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.85)', zIndex: 1
              }}>
                <div style={spinnerStyle} />
              </div>
            )}

            {messages.map((m, idx) => (
              <div key={idx} style={{ marginBottom: 10, textAlign: m.sender === 'user' ? 'right' : 'left' }}>
                <div
                  style={{
                    display: 'inline-block',
                    padding: '8px 12px',
                    borderRadius: 14,
                    background: m.sender === 'user' ? '#2563eb' : '#e5e7eb',
                    color: m.sender === 'user' ? '#fff' : '#111827',
                    maxWidth: '75%',
                    fontSize: 14,
                    boxShadow: m.sender === 'user'
                      ? '0 6px 16px rgba(37,99,235,0.25)'
                      : '0 4px 12px rgba(0,0,0,0.06)',
                       wordBreak: 'break-word',
 overflowWrap: 'anywhere',
 minWidth: 0
                  }}
                >
<RichText style={{"color":"white"}}  text={m.text} />


{m.attachments && m.attachments.length > 0 && (
  <div style={{ marginTop: 8 }}>
    {m.attachments.map((a, i) => {
      if (a.type === 'image') {
        return <img key={i} src={a.url} alt={a.name} style={{ maxWidth: 220, borderRadius: 8, display: 'block', marginTop: 6 }} />;
      } else if (a.type === 'audio') {
        return  <audio
    key={i}
    controls
    preload="metadata"
    src={a.url}
    style={{ display: 'block', marginTop: 6, maxWidth: 240 }}
  />
        // <audio key={i} controls src={a.url} style={{ display: 'block', marginTop: 6, maxWidth: 240 }} />;
      }
      return <a
  href={a.url}
  target="_blank"
  rel="noreferrer"
  style={{ color: '#fff', textDecoration: 'underline', wordBreak: 'break-word', overflowWrap: 'anywhere' }}
>
  {a.name}
</a>
    })}
  </div>
)}



                </div>

                {m.sender === 'bot' && m.response_id && !m.saved && (
                  <div
                    onClick={() => handleSaveToKnowledgebase(m.response_id, m.podio_refs, idx)}
                    title="Save this answer to knowledgebase"
                    style={{
                      cursor: 'pointer', fontSize: 12, marginTop: 4,
                      display: 'flex', gap: 6, alignItems: 'center', color: '#2563eb'
                    }}
                  >
                    <FaBookBookmark size={14} />
                    <span>Save to KB</span>
                  </div>
                )}
              </div>
            ))}


            {seedLoading && (
  <div style={{ fontStyle: 'italic', color: '#6b7280', fontSize: 14 }}>
    Checking CRM for details related to this number…
  </div>
)}
{!seedLoading && loading && (
  <div style={{ fontStyle: 'italic', color: '#6b7280', fontSize: 14 }}>
    Typing...
  </div>
)}


            <div ref={messageEndRef} />
          </div>

          {/* Input */}
       <div style={{ padding: 10, borderTop: '1px solid #e5e7eb' }}>
  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
    <button
      type="button"
      title="Attach files"
      onClick={() => fileInputRef.current?.click()}
      style={{
        width: 36, height: 36, borderRadius: '50%', border: 'none',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'linear-gradient(135deg,#4144f5 0%, #37bcfa 100%)',
        boxShadow: '0 6px 14px rgba(0,0,0,0.15)', cursor: 'pointer'
      }}
    >
      <Paperclip size={16} color="#fff" />
    </button>
    <input
      value={input}
      onChange={(e) => setInput(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && !loading && (input.trim() || selectedFiles.length > 0)) {
          sendQuery(input.trim());
          setInput('');
        }
      }}
      placeholder="Type a message…"
      disabled={loading}
      style={{ flex: 1, padding: 10, borderRadius: 25, border: '1px solid #e5e7eb', fontSize: 14 }}
    />
    <input
      ref={fileInputRef}
      type="file"
      multiple
      style={{ display: 'none' }}
      accept="image/*,audio/*"
      onChange={(e) => setSelectedFiles(Array.from(e.target.files || []))}
      // To allow ALL file types, remove the `accept` line above or set: accept="*/*"
    />
  </div>
  {selectedFiles.length > 0 && (
    <div style={{ marginTop: 6, fontSize: 12, color: '#6b7280' }}>
      {selectedFiles.length} file(s) selected
    </div>
  )}
</div>

        </div>
      </div>
    </>
  );
}





// // New with beautiful UI

// // File: LogsChatAssistantBox.js
// import React, { useEffect, useRef, useState } from 'react';
// import axios from 'axios';
// import { X, Maximize2, Paperclip, Send } from 'react-feather';
// import { FaBookBookmark, FaWandMagicSparkles } from 'react-icons/fa6';

// /* ==============================
//    Minimal Rich Text (bold + lists)
//    ============================== */
// function renderBold(str) {
//   return str.split(/(\*\*[^*]+\*\*)/g).map((seg, i) => {
//     const m = seg.match(/^\*\*([^*]+)\*\*$/);
//     if (m) return <strong key={i} style={{ fontWeight: 700 }}>{m[1]}</strong>;
//     return <span key={i}>{seg}</span>;
//   });
// }
// function preprocessForLists(text) {
//   if (!text) return '';
//   let s = String(text);
//   s = s.replace(/\s-\s+/g, '\n- ');
//   s = s.replace(/\s(\d+)\.\s+/g, '\n$1. ');
//   return s;
// }
// function RichText({ text, color = 'inherit' }) {
//   if (!text) return null;
//   const wrap = { wordBreak: 'break-word', overflowWrap: 'anywhere' };
//   const stylesRT = {
//     p: { margin: '0 0 6px 0', lineHeight: 1.45, color, ...wrap },
//     ul: { margin: '4px 0 0 16px', padding: 0, color, ...wrap },
//     ol: { margin: '4px 0 0 16px', padding: 0, color, ...wrap }
//   };
//   const prepped = preprocessForLists(text);
//   const lines = prepped.split(/\n+/);
//   const nodes = [];
//   let listType = null; // 'ul' | 'ol' | null
//   let listItems = [];

//   const flushList = (keyBase) => {
//     if (!listType || listItems.length === 0) return;
//     if (listType === 'ul') {
//       nodes.push(
//         <ul key={`ul-${keyBase}`} style={stylesRT.ul}>
//           {listItems.map((li, i) => <li key={i} style={wrap}>{renderBold(li)}</li>)}
//         </ul>
//       );
//     } else {
//       nodes.push(
//         <ol key={`ol-${keyBase}`} style={stylesRT.ol}>
//           {listItems.map((li, i) => <li key={i} style={wrap}>{renderBold(li)}</li>)}
//         </ol>
//       );
//     }
//     listType = null;
//     listItems = [];
//   };

//   lines.forEach((raw, idx) => {
//     const line = raw.trim();
//     if (!line) return;
//     if (/^-\s+/.test(line)) {
//       const content = line.replace(/^-+\s+/, '');
//       if (listType && listType !== 'ul') flushList(idx);
//       listType = 'ul';
//       listItems.push(content);
//       return;
//     }
//     const numMatch = line.match(/^(\d+)\.\s+(.*)$/);
//     if (numMatch) {
//       const content = numMatch[2];
//       if (listType && listType !== 'ol') flushList(idx);
//       listType = 'ol';
//       listItems.push(content);
//       return;
//     }
//     flushList(idx);
//     nodes.push(<p key={`p-${idx}`} style={stylesRT.p}>{renderBold(line)}</p>);
//   });

//   flushList('end');
//   return <>{nodes}</>;
// }

// /* ==============================
//    Attachments mapper (S3 safe)
//    ============================== */
// function mapAttachmentsFromHistory(atts = []) {
//   return atts.map(a => {
//     const mime = (a.mimetype || '').toLowerCase();
//     const nameKey = (a.name || a.key || '').toLowerCase();
//     const isImg = mime.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp|svg)$/.test(nameKey);
//     const isAud = mime.startsWith('audio/') || /\.(mp3|m4a|aac|wav|ogg|oga|flac|opus)$/.test(nameKey);
//     return {
//       name: a.name || a.key || 'file',
//       url: a.url,
//       type: isImg ? 'image' : (isAud ? 'audio' : 'file')
//     };
//   });
// }

// /* ==============================
//    Styles / Animations
//    ============================== */
// const NOT_FOUND_RE = /no\s+user\s+in\s+crm\s+found/i;

// const spinnerCSS = `
// @keyframes spin { 0%{transform:rotate(0)} 100%{transform:rotate(360deg)} }
// @keyframes bounceDot {
//   0%, 80%, 100% { transform: scale(0); opacity: .5; }
//   40% { transform: scale(1); opacity: 1; }
// }
// `;
// const wandCSS = `
// .ai-wand-btn { outline: none !important; -webkit-tap-highlight-color: transparent; }
// .ai-wand-btn:focus,
// .ai-wand-btn:focus-visible,
// .ai-wand-btn:active { outline: none !important; }
// `;
// const mobileCSS = `
// @media (max-width: 600px) {
//   .ai-wrapper {
//     right: 0 !important;
//     bottom: 0 !important;
//     width: 100vw !important;
//     height: 90vh !important;
//   }
//   .ai-panel {
//     width: 100vw !important;
//     height: 85vh !important;
//   }
//   .ai-wand-btn {
//     right: 20px !important;
//     bottom: 16px !important;
//     width: 56px !important;
//     height: 56px !important;
//   }
// }
// `;

// export default function LogsChatAssistantBox({
//   isOpen,
//   onClose,
//   sessionId,
//   phoneForQuery,
//   toNumber,
//   fromNumber
// }) {
//   // Loading & state (unchanged behavior)
//   const [bootLoading, setBootLoading] = useState(false);
//   const [loading, setLoading] = useState(false);
//   const [messages, setMessages] = useState([]);
//   const [input, setInput] = useState('');
//   const [selectedFiles, setSelectedFiles] = useState([]);
//   const fileInputRef = useRef(null);

//   const [currentSession, setCurrentSession] = useState(sessionId || '');
//   const [collapsed, setCollapsed] = useState(false);
//   const [headerText, setHeaderText] = useState('');
//   const [hasHeaderData, setHasHeaderData] = useState(false);
//   const [seedLoading, setSeedLoading] = useState(false);
//   const messageEndRef = useRef(null);

//   // Helpers preserved
//   const makeAttachmentPreviews = (files) => Array.from(files).map(f => {
//     const url = URL.createObjectURL(f);
//     const t = (f.type || '').toLowerCase();
//     const isImg = t.startsWith('image/'); const isAud = t.startsWith('audio/');
//     return { name: f.name, url, type: isImg ? 'image' : (isAud ? 'audio' : 'file') };
//   });
//   const clearFileInput = () => { setSelectedFiles([]); if (fileInputRef.current) fileInputRef.current.value = ''; };

//   // Auth + base preserved
//   const authHeaders = {
//     'Content-Type': 'application/json',
//     'Authorization': `Bearer ${localStorage.getItem('token')}`
//   };
//   // const BASE = 'http://localhost:8000'; // local
//   const BASE = 'https://bot.mergeasy.com'; // production

//   // Effects preserved
//   useEffect(() => {
//     if (!isOpen) return;
//     setCurrentSession(sessionId || '');
//     setMessages([]);
//     setInput('');
//     setCollapsed(false);
//     setHeaderText('');
//     setHasHeaderData(false);
//     fetchHistoryOrSeed();
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [isOpen, sessionId]);

//   useEffect(() => {
//     if (messageEndRef.current) {
//       messageEndRef.current.scrollIntoView({ behavior: 'smooth' });
//     }
//   }, [messages, loading]);

//   // Open full chat (unchanged behavior)
//   const openFullScreen = () => {
//     const id = encodeURIComponent(currentSession || sessionId || '');
//     const phone = encodeURIComponent(phoneForQuery || '');
//     const toQ = encodeURIComponent(toNumber || '');
//     const fromQ = encodeURIComponent(fromNumber || '');
//     const url = `/ai/chat/${id}?phone=${phone}&to=${toQ}&from=${fromQ}`;
//     window.open(url, '_blank', 'noopener,noreferrer');
//   };

//   const maybeSetHeaderFromText = (txt) => {
//     if (!txt) return;
//     if (!NOT_FOUND_RE.test(String(txt))) {
//       setHeaderText(String(txt));
//       setHasHeaderData(true);
//     }
//   };

//   // Fetch history (unchanged logic)
//   const fetchHistoryOrSeed = async () => {
//     try {
//       setBootLoading(true);
//       const hist = await axios.post(
//         `${BASE}/chat/session-history`,
//         { session_id: sessionId },
//         { headers: authHeaders }
//       );
//       const items = hist?.data?.items || [];
//       if (Array.isArray(items) && items.length > 0) {
//         const mapped = [];
//         for (const it of items) {
//           const hasAtts = Array.isArray(it.attachments) && it.attachments.length > 0;
//           if ((it.query && it.query.trim() !== '') || hasAtts) {
//             mapped.push({
//               sender: 'user',
//               text: it.query || '',
//               attachments: mapAttachmentsFromHistory(it.attachments || [])
//             });
//           }
//           if (it.response) {
//             mapped.push({
//               sender: 'bot',
//               text: it.response,
//               response_id: it.id,
//               podio_refs: it.podio_refs,
//               saved: false
//             });
//           }
//         }
//         setMessages(mapped);
//         const firstBot = mapped.find(m => m.sender === 'bot');
//         if (firstBot) maybeSetHeaderFromText(firstBot.text);
//         setBootLoading(false);
//       } else {
//         setBootLoading(false);
//         await seedInitialQuery();
//       }
//     } catch (err) {
//       console.error('History fetch failed:', err);
//       setBootLoading(false);
//       await seedInitialQuery();
//     }
//   };

//   // Seed (unchanged behavior)
//   const seedInitialQuery = async () => {
//     const text = `Tell the details of the item whose phone number is ${phoneForQuery}, if found nothing then send Just mentioning - No User In CRM Found.?`;
//     setSeedLoading(true);
//     setLoading(true);
//     try {
//       const res = await axios.post(
//         `${BASE}/agent-query`,
//         {
//           text,
//           session_id: sessionId,
//           to: toNumber || '',
//           from: fromNumber || '',
//           ai_sense: "ai_chat_bot"
//         },
//         { headers: authHeaders }
//       );
//       const botMsg = res?.data?.response || 'No response';
//       const podioRefs = res?.data?.podio_refs || null;
//       const responseId = res?.data?.response_id || null;

//       setMessages(prev => [
//         ...prev,
//         { sender: 'bot', text: botMsg, podio_refs: podioRefs, response_id: responseId, saved: false }
//       ]);
//       maybeSetHeaderFromText(botMsg);
//       setCurrentSession(res?.data?.session_id || sessionId || '');
//     } catch (err) {
//       console.error('Initial seed failed:', err);
//       setMessages(prev => [...prev, { sender: 'bot', text: 'Something went wrong while starting the session.' }]);
//     } finally {
//       setSeedLoading(false);
//       setLoading(false);
//     }
//   };

//   // Send query (unchanged behavior)
//   const sendQuery = async (text) => {
//     if (!text && selectedFiles.length === 0) return;
//     setLoading(true);
//     try {
//       let res;

//       if (selectedFiles.length > 0) {
//         const form = new FormData();
//         form.append('text', text || '');
//         form.append('session_id', currentSession || sessionId);
//         form.append('to', toNumber || '');
//         form.append('from', fromNumber || '');
//         form.append('ai_sense', "ai_chat_bot");
//         selectedFiles.forEach(f => form.append('files', f, f.name));

//         const previews = makeAttachmentPreviews(selectedFiles);
//         setMessages(prev => [...prev, { sender: 'user', text, attachments: previews }]);

//         res = await axios.post(`${BASE}/agent-query`, form, { headers: { 'Authorization': authHeaders.Authorization } });
//         clearFileInput();
//       } else {
//         setMessages(prev => [...prev, { sender: 'user', text }]);
//         res = await axios.post(
//           `${BASE}/agent-query`,
//           {
//             text,
//             session_id: currentSession || sessionId,
//             to: toNumber || '',
//             from: fromNumber || '',
//             ai_sense: "ai_chat_bot"
//           },
//           { headers: authHeaders }
//         );
//       }

//       const botMsg = res?.data?.response || 'No response';
//       const podioRefs = res?.data?.podio_refs || null;
//       const responseId = res?.data?.response_id || null;

//       setMessages(prev => [
//         ...prev,
//         { sender: 'bot', text: botMsg, podio_refs: podioRefs, response_id: responseId, saved: false }
//       ]);
//       if (res?.data?.session_id) setCurrentSession(res.data.session_id);
//     } catch (err) {
//       console.error('sendQuery failed:', err);
//       setMessages(prev => [...prev, { sender: 'bot', text: 'Something went wrong.' }]);
//     } finally {
//       setLoading(false);
//       setSelectedFiles([]);
//       setInput('');
//     }
//   };

//   // Save to KB (unchanged behavior)
//   const handleSaveToKnowledgebase = async (responseId, podioRefs, index) => {
//     try {
//       const res = await axios.post(
//         `${BASE}/podio/index-by-response`,
//         { response_id: responseId, podio_refs: podioRefs },
//         { headers: authHeaders }
//       );
//       if (res.status === 200) {
//         const next = [...messages];
//         if (next[index]) next[index].saved = true;
//         setMessages(next);
//       }
//     } catch (err) {
//       console.error('Failed to save to KB:', err);
//     }
//   };

//   // UI-only helpers (no logic change)
//   const onTextareaKeyDown = (e) => {
//     if (e.key === 'Enter' && !e.shiftKey) {
//       e.preventDefault();
//       if (!loading && (input.trim() || selectedFiles.length > 0)) {
//         sendQuery(input.trim());
//       }
//     }
//   };

//   if (!isOpen) return null;

//   /* ==============================
//      UI — Enhanced to match reference
//      ============================== */
//   return (
//     <>
//       <style>{spinnerCSS}</style>
//       <style>{wandCSS}</style>
//       <style>{mobileCSS}</style>

//       <div
//         className="ai-wrapper"
//         style={{
//           position: 'fixed',
//           right: 24,
//           bottom: 24,
//           zIndex: 2000,
//           display: 'flex',
//           flexDirection: 'column',
//           alignItems: 'flex-end',
//           gap: 12
//         }}
//       >
//         {/* Chat Panel */}
//         <div
//           className="ai-panel"
//           style={{
//             pointerEvents: collapsed ? 'none' : 'auto',
//             width: 400,
//             height: 600,
//             background: '#ffffff',
//             borderRadius: 16,
//             boxShadow: '0 18px 45px rgba(0,0,0,0.25)',
//             display: 'flex',
//             flexDirection: 'column',
//             transformOrigin: 'bottom right',
//             transform: collapsed ? 'scale(0.0)' : 'scale(1)',
//             opacity: collapsed ? 0 : 1,
//             transition: 'transform 280ms cubic-bezier(0.22,1,0.36,1), opacity 220ms ease'
//           }}
//         >
//           {/* Header */}
//           <div style={{ padding: 16, borderBottom: '1px solid #e5e7eb' }}>
//             <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
//               <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#111827' }}>✨ AI Assistant</h3>
//               <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#6b7280' }}>
//                 <button
//                   onClick={openFullScreen}
//                   title="Open in new tab"
//                   style={{
//                     border: 'none', background: 'transparent', cursor: 'pointer',
//                     borderRadius: 999, padding: 6
//                   }}
//                 >
//                   <Maximize2 size={18} />
//                 </button>
//                 <button
//                   onClick={() => {
//                     onClose();
//                   }}
//                   title="Close chat"
//                   style={{
//                     border: 'none', background: 'transparent', cursor: 'pointer',
//                     borderRadius: 999, padding: 6
//                   }}
//                 >
//                   <X size={18} />
//                 </button>
//               </div>
//             </div>

//             {hasHeaderData && (
//               <div
//                 style={{
//                   marginTop: 12,
//                   background: '#f8fafc',
//                   border: '1px solid #e2e8f0',
//                   borderRadius: 12,
//                   padding: 12,
//                   position: 'relative',
//                   overflow: 'hidden'
//                 }}
//               >
//                 <div
//                   style={{
//                     position: 'absolute',
//                     left: 0,
//                     top: 0,
//                     height: '100%',
//                     width: 6,
//                     background: 'linear-gradient(180deg, #22d3ee 0%, #3b82f6 100%)'
//                   }}
//                 />
//                 <div style={{ marginLeft: 8, maxHeight: 100, overflowY: 'auto', fontSize: 13, color: '#374151' }}>
//                   <RichText text={headerText} color="#111827" />
//                 </div>
//               </div>
//             )}
//           </div>

//           {/* Body */}
//           <div style={{ position: 'relative', flex: 1, padding: 16, overflowY: 'auto' }}>
//             {bootLoading && (
//               <div style={{
//                 position: 'absolute', inset: 0, display: 'flex',
//                 alignItems: 'center', justifyContent: 'center',
//                 background: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(2px)', zIndex: 10
//               }}>
//                 <div style={{
//                   width: 48, height: 48, border: '4px solid #e5e7eb',
//                   borderTop: '4px solid #3b82f6', borderRadius: '50%',
//                   animation: 'spin 1s linear infinite'
//                 }} />
//               </div>
//             )}

//             <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
//               {messages.map((m, idx) => (
//                 <div
//                   key={idx}
//                   style={{
//                     display: 'flex',
//                     alignItems: 'flex-end',
//                     justifyContent: m.sender === 'user' ? 'flex-end' : 'flex-start',
//                     gap: 8
//                   }}
//                 >
//                   <div
//                     style={{
//                       maxWidth: '80%',
//                       padding: 12,
//                       borderRadius: 16,
//                       background: m.sender === 'user' ? '#2563eb' : '#e5e7eb',
//                       color: m.sender === 'user' ? '#ffffff' : '#111827',
//                       boxShadow: m.sender === 'user'
//                         ? '0 8px 18px rgba(37,99,235,0.25)'
//                         : '0 6px 14px rgba(0,0,0,0.08)',
//                       ...(m.sender === 'user'
//                         ? { borderBottomRightRadius: 8 }
//                         : { borderBottomLeftRadius: 8 }),
//                       fontSize: 14,
//                       lineHeight: 1.45
//                     }}
//                   >
//                     <RichText text={m.text} color={m.sender === 'user' ? '#ffffff' : '#111827'} />

//                     {/* Attachments */}
//                     {m.attachments && m.attachments.length > 0 && (
//                       <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
//                         {m.attachments.map((a, i) => {
//                           if (a.type === 'image') {
//                             return (
//                               <img
//                                 key={i}
//                                 src={a.url}
//                                 alt={a.name}
//                                 style={{ maxWidth: '100%', height: 'auto', borderRadius: 10 }}
//                               />
//                             );
//                           }
//                           if (a.type === 'audio') {
//                             return (
//                               <audio
//                                 key={i}
//                                 controls
//                                 preload="metadata"
//                                 src={a.url}
//                                 style={{ display: 'block', width: '100%' }}
//                               />
//                             );
//                           }
//                           return (
//                             <a
//                               key={i}
//                               href={a.url}
//                               target="_blank"
//                               rel="noreferrer"
//                               style={{
//                                 color: m.sender === 'user' ? '#dbeafe' : '#1d4ed8',
//                                 textDecoration: 'underline',
//                                 wordBreak: 'break-word',
//                                 overflowWrap: 'anywhere'
//                               }}
//                             >
//                               {a.name}
//                             </a>
//                           );
//                         })}
//                       </div>
//                     )}

//                     {/* Save to KB (only bot, not saved) */}
//                     {m.sender === 'bot' && m.response_id && !m.saved && (
//                       <button
//                         onClick={() => handleSaveToKnowledgebase(m.response_id, m.podio_refs, idx)}
//                         title="Save to Knowledgebase"
//                         style={{
//                           marginTop: 8,
//                           display: 'inline-flex',
//                           alignItems: 'center',
//                           gap: 6,
//                           background: 'transparent',
//                           border: 'none',
//                           cursor: 'pointer',
//                           fontSize: 12,
//                           color: m.sender === 'user' ? '#dbeafe' : '#2563eb'
//                         }}
//                       >
//                         <FaBookBookmark size={13} />
//                         <span>Save to KB</span>
//                       </button>
//                     )}
//                   </div>
//                 </div>
//               ))}

//               {seedLoading && (
//                 <div style={{ fontSize: 13, color: '#6b7280', fontStyle: 'italic', textAlign: 'center', padding: '6px 0' }}>
//                   Checking CRM for details...
//                 </div>
//               )}

//               {!seedLoading && loading && (
//                 <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-start', gap: 8 }}>
//                   <div
//                     style={{
//                       background: '#e5e7eb',
//                       borderRadius: 16,
//                       borderBottomLeftRadius: 8,
//                       padding: 10,
//                       display: 'inline-flex',
//                       alignItems: 'center',
//                       gap: 6
//                     }}
//                   >
//                     {[0, 1, 2].map((d) => (
//                       <span
//                         key={d}
//                         style={{
//                           width: 8, height: 8, borderRadius: '50%', background: '#9ca3af',
//                           animation: 'bounceDot 1.4s infinite ease-in-out',
//                           animationDelay: `${d * 0.15}s`
//                         }}
//                       />
//                     ))}
//                   </div>
//                 </div>
//               )}
//             </div>

//             <div ref={messageEndRef} />
//           </div>

//           {/* Input */}
//           <div style={{ padding: 12, borderTop: '1px solid #e5e7eb' }}>
//             {selectedFiles.length > 0 && (
//               <div style={{ marginBottom: 6, paddingInline: 4, fontSize: 12, color: '#6b7280' }}>
//                 {selectedFiles.length} file(s): {selectedFiles.map(f => f.name).join(', ')}
//               </div>
//             )}
//             <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
//               <input
//                 ref={fileInputRef}
//                 type="file"
//                 multiple
//                 hidden
//                 accept="image/*,audio/*"
//                 onChange={(e) => setSelectedFiles(Array.from(e.target.files || []))}
//               />
//               <button
//                 type="button"
//                 title="Attach files"
//                 onClick={() => fileInputRef.current?.click()}
//                 style={{
//                   width: 36, height: 36, borderRadius: '50%', border: 'none',
//                   display: 'flex', alignItems: 'center', justifyContent: 'center',
//                   background: 'transparent', cursor: 'pointer'
//                 }}
//               >
//                 <Paperclip size={18} color="#6b7280" />
//               </button>

//               <textarea
//                 value={input}
//                 onChange={(e) => setInput(e.target.value)}
//                 onKeyDown={onTextareaKeyDown}
//                 placeholder="Type a message..."
//                 disabled={loading}
//                 rows={1}
//                 style={{
//                   flex: 1,
//                   padding: '10px 12px',
//                   fontSize: 14,
//                   background: '#f3f4f6',
//                   borderRadius: 10,
//                   border: '1px solid transparent',
//                   outline: 'none',
//                   resize: 'none'
//                 }}
//               />
//               <button
//                 onClick={() => sendQuery(input.trim())}
//                 disabled={loading || (!input.trim() && selectedFiles.length === 0)}
//                 style={{
//                   width: 36, height: 36,
//                   borderRadius: '50%',
//                   border: 'none',
//                   background: loading || (!input.trim() && selectedFiles.length === 0) ? '#d1d5db' : '#2563eb',
//                   color: '#fff',
//                   display: 'flex', alignItems: 'center', justifyContent: 'center',
//                   cursor: loading || (!input.trim() && selectedFiles.length === 0) ? 'not-allowed' : 'pointer',
//                   transition: 'transform 120ms ease'
//                 }}
//                 onMouseDown={e => (e.currentTarget.style.transform = 'scale(0.96)')}
//                 onMouseUp={e => (e.currentTarget.style.transform = 'scale(1)')}
//                 onMouseLeave={e => (e.currentTarget.style.transform = 'scale(1)')}
//                 title="Send"
//               >
//                 <Send size={16} />
//               </button>
//             </div>
//           </div>
//         </div>

//         {/* Wand Toggle */}
//         <button
//           className="ai-wand-btn"
//           onClick={() => setCollapsed(c => !c)}
//           title={collapsed ? 'Open Assistant' : 'Collapse Assistant'}
//           style={{
//             width: 64,
//             height: 64,
//             borderRadius: '50%',
//             border: 'none',
//             background: 'linear-gradient(135deg, #22d3ee 0%, #3b82f6 100%)',
//             color: '#fff',
//             boxShadow: '0 14px 28px rgba(0,0,0,0.25)',
//             display: 'flex',
//             alignItems: 'center',
//             justifyContent: 'center',
//             cursor: 'pointer',
//             transform: collapsed ? 'scale(1)' : 'scale(1.05)',
//             transition: 'transform 250ms ease'
//           }}
//           onMouseDown={e => (e.currentTarget.style.transform = 'scale(0.96)')}
//           onMouseUp={e => (e.currentTarget.style.transform = collapsed ? 'scale(1)' : 'scale(1.05)')}
//           onMouseLeave={e => (e.currentTarget.style.transform = collapsed ? 'scale(1)' : 'scale(1.05)')}
//         >
//           {collapsed ? <FaWandMagicSparkles size={22} /> : <X size={20} />}
//         </button>
//       </div>
//     </>
//   );
// }
